'use client'

import type { LucideIcon } from 'lucide-react'
import { ThemeToggle } from '@mentora/feature-components'
import { isAppAdminRole, splitRoles } from '@mentora/shared-components'
import { Button, Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@mentora/ui-components'
import {
  Bell,
  Building2,
  GraduationCap,
  LayoutGrid,
  Menu,
  Shield,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useState } from 'react'
import { LanguageSwitcher } from '@/components/language-switcher'
import { authClient } from '@/lib/auth-client'

function NavRow({
  href,
  icon: Icon,
  label,
  onNavigate,
}: {
  href: string
  icon: LucideIcon
  label: string
  onNavigate: () => void
}) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-foreground no-underline transition-colors hover:bg-muted"
    >
      <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden />
      {label}
    </Link>
  )
}

export function HeaderMobileNav() {
  const t = useTranslations('header')
  const [open, setOpen] = useState(false)
  const { data: session } = authClient.useSession()
  const { data: activeOrg } = authClient.useActiveOrganization()

  const close = () => setOpen(false)

  const isAppAdmin = isAppAdminRole(session?.user?.role)
  const currentMember = activeOrg?.members?.find(
    (m: { userId: string }) => m.userId === session?.user?.id,
  )
  const memberRoles = splitRoles(currentMember?.role ?? null)
  const isOrgAdminOrOwner = memberRoles.some(
    r => r === 'owner' || r === 'admin',
  )

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label={t('menuAriaLabel')}
        >
          <Menu className="size-5" strokeWidth={2} aria-hidden />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-sm">
        <SheetHeader>
          <SheetTitle>{t('menuTitle')}</SheetTitle>
        </SheetHeader>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 pb-6 pt-2" aria-label={t('menuTitle')}>
          <NavRow
            href="/explore"
            icon={LayoutGrid}
            label={t('explore')}
            onNavigate={close}
          />
          {session?.user
            ? (
                <NavRow
                  href="/learn"
                  icon={GraduationCap}
                  label={t('learn')}
                  onNavigate={close}
                />
              )
            : null}
          {isAppAdmin
            ? (
                <NavRow
                  href="/organizations"
                  icon={Shield}
                  label={t('appAdmin')}
                  onNavigate={close}
                />
              )
            : null}
          {isOrgAdminOrOwner
            ? (
                <NavRow
                  href="/organization"
                  icon={Building2}
                  label={t('organizationAdmin')}
                  onNavigate={close}
                />
              )
            : null}
          {session?.user
            ? (
                <NavRow
                  href="/notifications"
                  icon={Bell}
                  label={t('notifications')}
                  onNavigate={close}
                />
              )
            : null}

          <div className="my-3 border-t border-border" role="separator" />

          <div className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="text-sm text-muted-foreground">{t('languageLabel')}</span>
            <LanguageSwitcher />
          </div>
          <div className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="text-sm text-muted-foreground">{t('appearance')}</span>
            <ThemeToggle />
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  )
}
